import React from 'react';
import { useQueryClient } from 'react-query';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { Card } from '../../../../../../../../CommonComponents/OPL/Card/Card';
import { HtmlTable } from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Lozenge } from '../../../../../../../../CommonComponents/OPL/Lozenge/Lozenge';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { toast } from '../../../../../../../../CommonComponents/OPL/Toast/Toast';
import { Tooltip } from '../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { PdfPageWrapper } from '../../../../../../../../PDFRenderer/PDFRenderer.styles';
import { useClientId } from '../../../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../../../hooks/useFpId';
import { useIsPdf } from '../../../../../../../hooks/useIsPdf';
import { queryKeys } from '../../../../../../api/queryKeys';
import { useLongevityAnalysisGenderMutation } from '../../../../../../api/useLongevityAnalysisGenderMutation';
import { Gender } from '../../../../../../api/useLongevityAnalysisQuery';
import { defaultGender, genderOptions } from '../../LongevityAnalysis.meta';
import { LoadingTable } from './Table.loading';
import { useTable } from './useTable';

export const Table = () => {
  const { data } = useTable();

  if (!data) {
    return null;
  }

  const { principalGender, spouseGender, principalName, spouseName } = data;

  return (
    <TableContent
      defaultPrincipalGender={principalGender}
      defaultSpouseGender={spouseGender}
      principalName={principalName}
      spouseName={spouseName}
    />
  );
};

interface Props {
  defaultPrincipalGender: Gender;
  defaultSpouseGender?: Gender;
  principalName: string;
  spouseName: string | null;
}

const TableContent = ({
  defaultPrincipalGender,
  defaultSpouseGender,
  principalName,
  spouseName,
}: Props) => {
  const { data } = useTable();
  const queryClient = useQueryClient();

  const clientId = useClientId();
  const fpId = useFpId();
  const isPdf = useIsPdf();

  const isFetchingData = queryClient.isFetching(queryKeys.longevity(clientId, fpId)) > 0;

  const [localSetGender, setLocalGetGender] = React.useState<{
    principal: Gender;
    spouse?: Gender;
  }>({
    principal: defaultPrincipalGender,
    spouse: defaultSpouseGender,
  });

  const { mutateAsync, isLoading } = useLongevityAnalysisGenderMutation();

  if (!data) {
    return null;
  }

  const { headers, tableData, hasSpouse } = data;

  return (
    <Card>
      {!isPdf && (
        <Box
          display="Flex"
          justifyContent="flex-end"
          css={({ theme }) => ({ gap: theme.space['16'] })}
          p="12"
        >
          <Box display="flex" alignItems="center" css={({ theme }) => ({ gap: theme.space['8'] })}>
            <Text variant="formLabel">{principalName} Gender:</Text>
            <Select
              value={genderOptions.find(({ value }) => value === localSetGender.principal)}
              options={genderOptions}
              onChange={opt => {
                if (opt) {
                  setLocalGetGender(state => ({ ...state, principal: opt.value }));
                }
              }}
              css={{ minWidth: 120 }}
            />
          </Box>

          {hasSpouse && (
            <Box
              display="flex"
              alignItems="center"
              css={({ theme }) => ({ gap: theme.space['8'] })}
            >
              <Text variant="formLabel">{spouseName} Gender:</Text>
              <Select
                value={genderOptions.find(({ value }) => value === localSetGender.spouse)}
                options={genderOptions}
                onChange={opt => {
                  if (opt) {
                    setLocalGetGender(state => ({ ...state, spouse: opt.value }));
                  }
                }}
                css={{ minWidth: 120 }}
              />
            </Box>
          )}

          <Button
            isLoading={isLoading}
            onClick={async () => {
              try {
                await mutateAsync({
                  principalGender: localSetGender.principal,
                  spouseGender: localSetGender.spouse || defaultGender,
                  clientId,
                  fpId,
                });

                queryClient.invalidateQueries(queryKeys.longevity(clientId, fpId));

                toast.success(`Gender${hasSpouse ? 's' : ''} have been saved successfully.`);
              } catch (err) {
                console.error(err);
                toast.error('An error occurred while saving genders. Please try again.');
              }
            }}
          >
            Save
          </Button>
        </Box>
      )}

      {isFetchingData ? (
        <LoadingTable />
      ) : (
        <PdfPageWrapper>
          <HtmlTable variant="basic">
            <table>
              <colgroup>
                <col width="20%" />
                <col width="10%" />
                <col width="23%" />
                {hasSpouse && <col width="23%" />}
                <col width="24%" />
              </colgroup>

              <thead>
                <tr>
                  {headers.map(({ header, tooltip }) => (
                    <th key={header}>
                      <Box
                        display="flex"
                        css={({ theme }) => ({ gap: theme.space['4'] })}
                        alignItems="center"
                      >
                        <Text
                          variant="tableHeader"
                          textAlign="left"
                          css={{
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                          }}
                        >
                          {header}
                        </Text>
                        {tooltip && (
                          <Tooltip content={tooltip} disabled={!tooltip}>
                            <Icon
                              className="far fa-info-circle"
                              color="neutral.500"
                              fontSize="small"
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {tableData.map((row, index) => {
                  const {
                    PrincipalAge,
                    PrincipalWorkingStatus,
                    ProbPrincipalBeingAlive,

                    SpouseAge,
                    SpouseWorkingStatus,
                    ProbAtleastOneAlive,

                    CalendarYear,
                    ProbBothBeingAlive,
                    MonteCarloProbabilityOfSuccess,
                  } = row;

                  return (
                    <tr key={index}>
                      <td>
                        <Box
                          display="flex"
                          css={({ theme }) => ({ gap: theme.space['8'] })}
                          justifyContent="space-between"
                        >
                          {PrincipalAge ? (
                            <Box
                              display="flex"
                              css={({ theme }) => ({ gap: theme.space['4'] })}
                              alignItems="center"
                            >
                              <Text textAlign="right" fontSize="small" fontWeight={500}>
                                {PrincipalAge}{' '}
                              </Text>

                              {PrincipalWorkingStatus !== 'Working' ? (
                                <Lozenge>{PrincipalWorkingStatus}</Lozenge>
                              ) : (
                                <Lozenge variantColor="new">{PrincipalWorkingStatus}</Lozenge>
                              )}
                            </Box>
                          ) : (
                            <Box display="flex" justifyContent="center" flex={1}>
                              <Text textAlign="right" fontSize="small" fontWeight={500}>
                                -
                              </Text>
                            </Box>
                          )}

                          {SpouseAge ? (
                            <Box display="flex" css={({ theme }) => ({ gap: theme.space['4'] })}>
                              <Text>{SpouseAge && PrincipalAge ? ',' : ''}</Text>
                              <Box
                                display="flex"
                                css={({ theme }) => ({ gap: theme.space['4'] })}
                                alignItems="center"
                              >
                                <Text textAlign="right" fontSize="small" fontWeight={500}>
                                  {SpouseAge}{' '}
                                </Text>

                                {SpouseWorkingStatus !== 'Working' ? (
                                  <Lozenge>{SpouseWorkingStatus}</Lozenge>
                                ) : (
                                  <Lozenge variantColor="new">{SpouseWorkingStatus}</Lozenge>
                                )}
                              </Box>
                            </Box>
                          ) : hasSpouse ? (
                            <Box display="flex" justifyContent="center" flex={1}>
                              <Text textAlign="right" fontSize="small" fontWeight={500}>
                                -
                              </Text>
                            </Box>
                          ) : null}
                        </Box>
                      </td>

                      <td>
                        <Text textAlign="left" fontSize="small" fontWeight={500}>
                          {CalendarYear}
                        </Text>
                      </td>

                      <td>
                        <Text textAlign="left" fontSize="small" fontWeight={500}>
                          {hasSpouse
                            ? formatPercentage(ProbAtleastOneAlive, {
                                decimalPlaces: 1,
                              })
                            : ProbPrincipalBeingAlive !== null
                            ? formatPercentage(ProbPrincipalBeingAlive, {
                                decimalPlaces: 1,
                              })
                            : '-'}
                        </Text>
                      </td>

                      {hasSpouse && (
                        <td>
                          <Text textAlign="left" fontSize="small" fontWeight={500}>
                            {ProbBothBeingAlive
                              ? formatPercentage(ProbBothBeingAlive, {
                                  decimalPlaces: 1,
                                })
                              : '-'}
                          </Text>
                        </td>
                      )}

                      <td>
                        <Text textAlign="left" fontSize="small" fontWeight={500}>
                          {formatPercentage(MonteCarloProbabilityOfSuccess)}
                        </Text>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </HtmlTable>
        </PdfPageWrapper>
      )}
    </Card>
  );
};
